import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../../Header';
import Footer from '../../../Footer';
import { getProcurementDemand } from '../../../../api/procurementApi';
import { extractApiArray, getApiErrorText } from '../../../../utils/apiResponse';
import { checkIsAdminLoggined } from '../../../../utils/utils';
import ProcurementDesktopView from './ProcurementDesktopView';
import ProcurementMobileView from './ProcurementMobileView';
import {
  groupProcurementDemand,
  procurementTotals,
  PRODUCT_STATUS_LABELS,
} from './procurementUtils';
import '../../../../styles/procurement-audit.css';

const PAGE_SIZE = 25;
export default function ProcurementAuditPage() {
  const history = useHistory();
  const location = useLocation();
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadedAt, setLoadedAt] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState([]);
  const toggleExpand = productId => {
    setExpandedProducts(previous =>
      previous.includes(productId)
        ? previous.filter(id => id !== productId)
        : [...previous, productId],
    );
  };
  const requestId = useRef(0);
  const mounted = useRef(false);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 991.98px)').matches);
  const params = new URLSearchParams(location.search);
  const search = params.get('search') || '';
  const status = params.get('status') || '';
  const sort = ['oldest', 'quantity', 'name'].includes(params.get('sort'))
    ? params.get('sort')
    : 'oldest';
  const requestedPage = Math.max(1, Number.parseInt(params.get('page'), 10) || 1);
  const groups = useMemo(() => groupProcurementDemand(lines, { search, status, sort }), [
    lines,
    search,
    status,
    sort,
  ]);
  const totals = procurementTotals(groups);
  const pageCount = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  const page = Math.min(requestedPage, pageCount);
  const visibleProducts = groups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const updateFilters = changes => {
    const next = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key),
    );
    if (!Object.prototype.hasOwnProperty.call(changes, 'page')) next.delete('page');
    history.replace({ pathname: location.pathname, search: next.toString() ? `?${next}` : '' });
  };
  const reload = useCallback(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    getProcurementDemand()
      .then(response => {
        if (!mounted.current || id !== requestId.current) return;
        setLines(extractApiArray(response));
        setLoadedAt(new Date());
      })
      .catch(err => {
        if (!mounted.current || id !== requestId.current) return;
        setError(getApiErrorText(err, 'Не вдалося завантажити реєстр'));
        setLines([]);
        setLoadedAt(null);
      })
      .finally(() => {
        if (mounted.current && id === requestId.current) setLoading(false);
      });
  }, []);
  useEffect(() => {
    mounted.current = true;
    checkIsAdminLoggined({ history });
    reload();
    const refreshVisible = () => {
      if (!document.hidden) reload();
    };
    const interval = setInterval(refreshVisible, 60000);
    window.addEventListener('focus', refreshVisible);
    return () => {
      mounted.current = false;
      clearInterval(interval);
      window.removeEventListener('focus', refreshVisible);
    };
  }, [history, reload]);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 991.98px)');
    const update = () => setMobile(media.matches);
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);
  return (
    <div className='container'>
      <Helmet>
        <title>Товари до замовлення — Wargame Shop</title>
      </Helmet>
      <Header />
      <main className='procurement-page py-3'>
        <div className='d-flex justify-content-between align-items-start mb-2'>
          <h3 className='font-weight-bold mb-0'>Товари до замовлення</h3>
          <button
            className='btn btn-outline-dark ml-2'
            onClick={reload}
            disabled={loading}
            aria-label='Оновити реєстр'
          >
            <i
              className={`bi ${loading ? 'bi-hourglass-split' : 'bi-arrow-clockwise'}`}
              aria-hidden='true'
            />
          </button>
        </div>
        <p className='text-muted small'>
          Товари активних невідправлених замовлень, які ще очікують надходження на склад.
        </p>
        <div className='procurement-summary mb-3' aria-label='Підсумок реєстру'>
          <span>
            Товарів: <b>{error || (loading && !loadedAt) ? '—' : totals.products}</b>
          </span>
          <span>
            Замовлень: <b>{error || (loading && !loadedAt) ? '—' : totals.orders}</b>
          </span>
          <span>
            Не забезпечено: <b>{error || (loading && !loadedAt) ? '—' : `${totals.missing} шт`}</b>
          </span>
          <span>
            Ще замовити: <b>{error || (loading && !loadedAt) ? '—' : `${totals.toOrder} шт`}</b>
          </span>
        </div>
        <div className='procurement-filters mb-3'>
          <input
            className='form-control'
            aria-label='Пошук товару або замовлення'
            placeholder='Назва, артикул, ID товару або замовлення'
            value={search}
            onChange={event => updateFilters({ search: event.target.value })}
          />
          <select
            className='form-control'
            aria-label='Статус товару'
            value={status}
            onChange={event => updateFilters({ status: event.target.value })}
          >
            <option value=''>Усі статуси товарів</option>
            {Object.entries(PRODUCT_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            className='form-control'
            aria-label='Сортування'
            value={sort}
            onChange={event => updateFilters({ sort: event.target.value })}
          >
            <option value='oldest'>Найдавніші замовлення</option>
            <option value='quantity'>Найбільша потреба</option>
            <option value='name'>За назвою товару</option>
          </select>
        </div>
        <div className='small text-muted mb-3'>
          «Ще замовити» враховує вільний складський залишок і позиції зі статусом «Замовлено».
          Повернені позиції не входять до потреби закупівель. Підсумки відповідають фільтрам.
        </div>
        {loadedAt && (
          <div className='small text-muted mb-2'>
            Оновлено: {loadedAt.toLocaleTimeString('uk-UA')} · автоматично щохвилини
          </div>
        )}
        {error && (
          <div className='alert alert-danger' role='alert'>
            {error}
            <button className='btn btn-sm btn-outline-danger ml-2' onClick={reload}>
              Спробувати ще раз
            </button>
          </div>
        )}
        {loading && (
          <div role='status' className='text-muted mb-3'>
            Оновлення реєстру…
          </div>
        )}
        {!error && !loading && groups.length === 0 && (
          <div className='alert alert-light border'>
            {search || status
              ? 'За цими фільтрами товарів немає.'
              : 'Усі позиції активних замовлень забезпечені зі складу.'}
          </div>
        )}
        {!error &&
          groups.length > 0 &&
          (mobile ? (
            <ProcurementMobileView
              products={visibleProducts}
              expandedProducts={expandedProducts}
              onToggleExpand={toggleExpand}
            />
          ) : (
            <ProcurementDesktopView
              products={visibleProducts}
              expandedProducts={expandedProducts}
              onToggleExpand={toggleExpand}
            />
          ))}
        {!error && pageCount > 1 && (
          <nav
            className='d-flex justify-content-between align-items-center my-3'
            aria-label='Сторінки реєстру'
          >
            <button
              className='btn btn-outline-dark'
              disabled={page === 1}
              onClick={() => updateFilters({ page: page - 1 })}
            >
              Назад
            </button>
            <span>
              {page} / {pageCount}
            </span>
            <button
              className='btn btn-outline-dark'
              disabled={page === pageCount}
              onClick={() => updateFilters({ page: page + 1 })}
            >
              Далі
            </button>
          </nav>
        )}
      </main>
      <Footer />
    </div>
  );
}
